'use client';

import type { Currency } from '@shared/utils';
import { createContext, useContext, useState, type ReactNode } from 'react';
import type { LotteryName } from '../../../../../lottery-purchases/types';
import type { AdditionalDrawingInfo, ExtraGame, OddsEntry, PrizeTableTooltips, ResultsConfig, TableData } from '../../../../types';
import type { DataMapper } from '../../utils';

type PrizeTablesProviderValues = {
  lotteryName: LotteryName;
  currency: Currency;
  data: OddsEntry[];
  drawingDate: string;
  namespace: string;
  extraGames: ExtraGame[];
  multiplier: number;
  multipliers: number[];
  dataMapper: DataMapper;
  config?: ResultsConfig;
  additionalDrawingInfo?: AdditionalDrawingInfo;
  className?: string;
  tooltips?: PrizeTableTooltips;
};

type PrizeTablesContextValue = {
  lotteryName: LotteryName;
  drawingDate: string;
  tables: TableData[];
  namespace: string;
  extraGames: ExtraGame[];
  multiplier: number;
  multipliers: number[];
  setMultiplier: (multiplier: number) => void;
  additionalDrawingInfo?: AdditionalDrawingInfo;
  className?: string;
  tooltips?: PrizeTableTooltips;
};

const PrizeTablesContext = createContext<PrizeTablesContextValue | undefined>(undefined);

type PrizeTablesProviderProps = {
  children: ReactNode;
  value: PrizeTablesProviderValues;
};

function PrizeTablesProvider({ children, value }: PrizeTablesProviderProps) {
  const { lotteryName, drawingDate, data, currency, config, dataMapper, namespace, extraGames, className, multipliers, multiplier, additionalDrawingInfo, tooltips } = value;
  const [selectedMultiplier, setSelectedMultiplier] = useState(multiplier);

  const tables = dataMapper({
    data,
    namespace: [namespace, `lotteries.results`],
    currency,
    extraGames,
    hideWinners: !!config?.hideWinners,
    multiplier: selectedMultiplier,
    additionalDrawingInfo: additionalDrawingInfo ?? {},
  }).sort((aa, bb) => {
    if (aa.lotteryId === lotteryName) return -1;
    if (bb.lotteryId === lotteryName) return 1;
    return 0;
  });

  const providerValues = {
    lotteryName,
    drawingDate,
    tables,
    namespace,
    extraGames,
    className,
    tooltips,
    multipliers,
    multiplier: selectedMultiplier,
    setMultiplier: setSelectedMultiplier,
  };
  return <PrizeTablesContext.Provider value={providerValues}>{children}</PrizeTablesContext.Provider>;
}

function usePrizeTablesContext() {
  const context = useContext(PrizeTablesContext);
  if (context === undefined) {
    throw new Error('usePrizeTablesContext must be used within a PrizeTablesProvider');
  }
  return context;
}

export { PrizeTablesProvider, usePrizeTablesContext };
export type { PrizeTablesContextValue, TableData };
